import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Terms() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gray-900 text-white py-20 pt-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-6">STASHLY PTY LTD – TERMS AND CONDITIONS</h1>
          <p className="text-xl text-gray-300">
            Last Updated: April 9, 2026
          </p>
        </div>
      </section>

      {/* Acceptance of Terms - NEW SECTION */}
      <section className="bg-white border-b-2 border-gray-200 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            By accessing or using the Stashly Platform, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, you must not use our Service.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            These Terms constitute a legally binding agreement between you and Stashly Technologies Pty Ltd. Your use of the Platform signifies your acceptance of these Terms.
          </p>
        </div>
      </section>

      {/* Australian Consumer Law Notice */}
      <section className="bg-blue-50 border-b-4 border-blue-600 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <i className="ri-information-fill text-blue-600 text-3xl mt-1"></i>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">IMPORTANT: Your Consumer Guarantees</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                If you are a consumer for the purposes of the Australian Consumer Law, our services come with consumer guarantees that cannot be excluded. These Terms should be read together with your statutory rights under that law. Nothing in this document removes or restricts those rights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Definitions */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">1. DEFINITIONS</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                In these Terms, unless the context requires otherwise:
              </p>
              <div className="bg-gray-50 rounded-xl p-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="py-3 pr-4 text-left font-bold text-gray-900">Term</th>
                      <th className="py-3 text-left font-bold text-gray-900">Definition</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Account</td>
                      <td className="py-3 text-gray-700">A registered user profile created on the Stashly platform.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Booking</td>
                      <td className="py-3 text-gray-700">A confirmed reservation of a Space by a Renter for a defined period, made through the Stashly platform.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Host</td>
                      <td className="py-3 text-gray-700">A user who lists and makes a Space available for storage through the Stashly platform.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Renter</td>
                      <td className="py-3 text-gray-700">A user who books and stores property in a Space through the Stashly platform.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Space</td>
                      <td className="py-3 text-gray-700">Any physical area, structure, room, container, garage, storage unit, or other location listed by a Host for storage purposes.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Stored Items</td>
                      <td className="py-3 text-gray-700">Any personal property, goods, belongings, or effects placed or proposed to be placed in a Space by a Renter.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Stashly, "we", "us", or "our"</td>
                      <td className="py-3 text-gray-700">Stashly Technologies Pty Ltd ACN 696 643 713, trading as Stashly.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Platform</td>
                      <td className="py-3 text-gray-700">The Stashly website, mobile application, and associated services.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Host Protection Programme</td>
                      <td className="py-3 text-gray-700">The optional, discretionary support programme described in Section 12, which is not insurance and does not create legal rights or obligations.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">User, "you", or "your"</td>
                      <td className="py-3 text-gray-700">Any individual or entity that accesses, uses, or registers with the Stashly platform, including Hosts and Renters.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Fees</td>
                      <td className="py-3 text-gray-700">The charges, commissions, service fees, or other amounts payable to Stashly for use of the platform or for facilitating a Booking.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Term</td>
                      <td className="py-3 text-gray-700">The duration for which these Terms apply to a user.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Applicable Law</td>
                      <td className="py-3 text-gray-700">All legislation, regulations, by-laws, local laws, Australian Consumer Law provisions, and court decisions that apply to the Space, the Host, the Renter, or the use of the Stashly platform.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Business Day</td>
                      <td className="py-3 text-gray-700">A day that is not a Saturday, Sunday, or public holiday in the state or territory in which the Space is located.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Consumer</td>
                      <td className="py-3 text-gray-700">Any natural person who is acquiring services ordinarily acquired for personal, domestic or household use or consumption.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Australian Consumer Law or ACL</td>
                      <td className="py-3 text-gray-700">The Australian Consumer Law found in Schedule 2 of the Competition and Consumer Act 2010 (Cth).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* About Stashly - NEW SECTION */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">2. ABOUT STASHLY</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">2.1 Marketplace Platform</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Stashly is an online marketplace platform that enables Hosts to offer Spaces for storage and Renters to book those Spaces. Stashly does not own, operate, manage, or control any Spaces.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">2.2 Not a Storage Provider</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Stashly does not provide storage services, does not take possession of Stored Items, and is not a warehouse, bailee, carrier, landlord, or insurer.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">2.3 Direct Agreement Between Users</h4>
                  <p className="text-gray-700 leading-relaxed">
                    When a Renter books a Space, a direct storage agreement is formed between the Host and the Renter. Stashly is not a party to that agreement, except for limited payment facilitation purposes as described in these Terms.
                  </p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-3">2.4 No Guarantees</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Stashly does not guarantee the suitability, legality, safety, or condition of any Space.
                  </p>
                </div>
              </div>
            </div>

            {/* Australian Consumer Law */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">3. AUSTRALIAN CONSUMER LAW – CONSUMER GUARANTEES</h2>
              
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">3.1 Non-Excludable Guarantees</h4>
                <p className="text-gray-700 leading-relaxed">
                  If you are a "consumer" for the purposes of the Australian Consumer Law (ACL), our services come with consumer guarantees that cannot be excluded, restricted or modified. These guarantees include that services will be provided with due care and skill, will be reasonably fit for any purpose disclosed, and will be supplied within a reasonable time.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">3.2 Statutory Rights Not Excluded</h4>
                <p className="text-gray-700 leading-relaxed">
                  Nothing in these Terms excludes, restricts or modifies any consumer guarantees, rights or remedies you may have under the ACL or any other law that cannot be excluded. If you are a consumer and any part of these Terms seeks to exclude or restrict a non-excludable guarantee or right, that part is void to the extent of the inconsistency.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">3.3 Limited Liability for Business Users</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Where we supply services to you and you are not a consumer, or where the services are not of a kind ordinarily acquired for personal, domestic or household use or consumption, then to the extent permitted by section 64A of the Australian Consumer Law, our liability for failure to comply with any consumer guarantee is limited, at our option, to:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">(a) supplying the services again; or</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">(b) paying the cost of having the services supplied again.</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">3.4 ACL Overrides</h4>
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    To the extent of any inconsistency between these Terms and the ACL, the ACL prevails.
                  </p>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">4. ELIGIBILITY</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">4.1 Age and Legal Capacity</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users must be at least 18 years old and capable of entering a binding legal agreement.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">4.2 Accurate Information</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users must provide accurate, current, and complete information when registering an Account.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">4.3 Account Suspension</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Stashly may suspend or terminate Accounts where false information is provided or eligibility is not met.
                  </p>
                </div>
              </div>
            </div>

            {/* User Accounts */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">5. USER ACCOUNTS</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">5.1 Account Confidentiality</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users are responsible for maintaining the confidentiality of their Account credentials.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">5.2 Account Activity</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Users are responsible for all activity conducted under their Account, whether authorised or not.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Certain platform features require identity verification to ensure trust and safety. Users must complete verification when requested and maintain accurate verification information. Failure to verify may result in restricted account access. See Section 19.6 for detailed information about our identity verification and document handling policies.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">5.3 Unauthorised Access</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users must notify Stashly immediately of any unauthorised access or suspected misuse of their Account by emailing support@stashly.com.au or using the "Report Misuse" function on the Platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Platform Services */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">6. USE OF THE PLATFORM</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">6.1 Compliance with Applicable Laws</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users must comply with all Applicable Laws when using the Platform.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">6.2 Prohibited Conduct</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Users must not:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(a) engage in illegal, fraudulent, or harmful conduct;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(b) misuse or interfere with the Platform;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(c) circumvent Fees or payment processes;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(d) use the Platform to store prohibited items or conduct unsafe activities;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(e) harass, threaten, or defame other users.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">6.3 Platform Modifications</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Stashly may modify, update, or restrict access to the Platform at its discretion, provided such action is done in accordance with these Terms and Applicable Law.
                  </p>
                </div>
              </div>
            </div>

            {/* Host Responsibilities */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">7. HOST RESPONSIBILITIES</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.1 Space Safety and Suitability</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Hosts must ensure that any Space listed on the Platform is safe, lawful, clean, and reasonably suitable for storage.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.2 Legal Compliance</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Hosts must comply with all Applicable Laws, including:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(a) zoning rules and land use restrictions;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(b) building codes and structural safety requirements;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(c) fire safety regulations;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(d) body corporate by-laws and lease conditions;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(e) mortgage requirements and restrictions;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(f) insurance obligations;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(g) council and local government requirements.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.3 Material Disclosure</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Hosts must disclose any material risks, restrictions, or conditions relating to the Space before accepting a Booking, including:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(a) environmental hazards (asbestos, mould, water ingress, pests);</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(b) structural defects or limitations;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(c) access restrictions or security concerns;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(d) insurance or lease limitations on who may use the Space.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.4 Access to Stored Items</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Hosts must not access a Renter's Stored Items except:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(a) with the Renter's express consent;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(b) in an emergency involving immediate risk to life, safety or property;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                      <span className="text-gray-700">(c) as required by law or a court order.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.5 Space Maintenance</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Hosts are responsible for maintaining the Space in a condition that does not present unreasonable hazards to Renters or third parties.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.6 Access Arrangements</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Hosts must ensure that access arrangements and instructions are accurate and do not expose Renters to unreasonable risk.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">7.7 Third Party Compliance</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Hosts must ensure that any person they allow to access the Space (including family members, guests, contractors, or third parties) complies with these Terms and does not interfere with Renter's Stored Items.
                  </p>
                </div>
              </div>
            </div>

            {/* Renter Responsibilities - NEW SECTION */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">8. RENTER RESPONSIBILITIES</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.1 Safe and Lawful Items</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters must ensure that all Stored Items are safe, lawful, and permitted under these Terms.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.2 Prohibited Items</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters must not store any prohibited items listed in Section 9.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.3 Proper Packaging and Security</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters must ensure that Stored Items are properly packaged, protected, and secured inside the Space to prevent damage, loss, or hazard.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.4 Liability for Damage</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Renters must not damage the Space or Host property. Renters are liable for any loss or damage caused by:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(a) their actions or omissions;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(b) their Stored Items;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(c) any person they allow to access the Space;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(d) failure to maintain or properly package Stored Items.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.5 Access Compliance</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters must follow all reasonable access arrangements and comply with any safety instructions provided by the Host.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.6 Space Condition</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters must leave the Space clean and in the same condition as at the start of the Booking, reasonable wear and tear excepted.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">8.7 Timely Removal</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters must ensure timely removal of all Stored Items at the end of the Booking and must vacate the Space by the agreed end date.
                  </p>
                </div>
              </div>
            </div>

            {/* Prohibited Items */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">9. PROHIBITED ITEMS</h2>
              
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">9.1 Items That Must Not Be Stored</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Renters must not store any of the following items under any circumstances:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Flammable, explosive, or combustible materials (petrol, paint, solvents, aerosols, fireworks)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Hazardous substances, chemicals, or toxic materials (asbestos, lead paint, biohazardous waste)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Illegal goods or contraband</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Weapons, ammunition, firearms, or components thereof</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Perishable goods, food, plants, or living creatures</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Waste, biological material, or medical waste</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Cash, jewellery, precious metals, art, or high-value collectibles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Items requiring temperature or humidity control unless the Space is designed for that purpose</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Items that may attract pests, encourage mould growth, or risk contamination of the Space</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">9.2 Updates to Prohibited Items</h4>
                <p className="text-gray-700 leading-relaxed">
                  Stashly may update the list of prohibited items from time to time. Updated lists will be posted on the Platform with reasonable notice.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">9.3 Additional Host Restrictions</h4>
                <p className="text-gray-700 leading-relaxed">
                  Hosts may impose additional restrictions on what may be stored in their Space, provided such restrictions are clearly disclosed in the Listing before a Booking is accepted.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">9.4 Consequences of Breach</h4>
                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    If a Renter stores prohibited items or breaches this section, Stashly and/or the Host may immediately terminate the Booking and require removal of all Stored Items. Costs of removal and proper disposal may be charged to the Renter.
                  </p>
                </div>
              </div>
            </div>

            {/* Behaviour and Use */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">10. BEHAVIOUR AND USE OF SPACES</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">10.1 Respectful Conduct</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users must behave respectfully and lawfully when interacting with other Users and accessing Spaces.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">10.2 Prohibited Conduct</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Users must not:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(a) engage in threatening, aggressive, intimidating, or inappropriate behaviour;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(b) interfere with neighbouring properties, common areas, or other residents;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(c) use a Space for habitation, business activities, or purposes other than storage;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(d) store items outside the designated Space;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(e) bypass, tamper with, or damage security systems or access equipment;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(f) create unreasonable noise or disturbance;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(g) conduct illegal activities in or around the Space.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">10.3 Account Suspension</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Stashly may suspend or terminate a User's Account for unsafe, dishonest, disruptive, or illegal conduct.
                  </p>
                </div>
              </div>
            </div>

            {/* Access to Spaces */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">11. ACCESS TO SPACES</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">11.1 Access Times</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters may only access a Space during the times and in the manner agreed with the Host in the Listing or Booking confirmation.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">11.2 Reasonable Access</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Hosts must provide reasonable access to Stored Items upon request from the Renter, subject to reasonable notice (typically 24 hours) unless otherwise specified in the Listing.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">11.3 Access Modifications</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Hosts may modify access arrangements where necessary for safety, maintenance, or legal compliance, provided reasonable notice is given to the Renter.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">11.4 Access Restrictions</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Hosts may refuse or restrict access if:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(a) the Renter breaches these Terms or the Booking agreement;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(b) access would create an immediate safety risk;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(c) required by law or a court order;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(d) access is sought outside agreed times without legitimate emergency.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">11.5 Third Party Access</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Renters are responsible for ensuring that any person they bring to the Space (family members, friends, contractors) complies with these Terms and does not interfere with the Space or damage Host property.
                  </p>
                </div>
              </div>
            </div>

            {/* Liability, Risk and Release */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">12. LIABILITY, RISK AND RELEASE</h2>
              
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.1 Stashly Is Not a Party to Storage Transactions</h4>
                <p className="text-gray-700 leading-relaxed">
                  Stashly operates solely as an online marketplace platform. Hosts and Renters enter into storage arrangements directly with one another. Stashly is not a party to any storage agreement and does not assume responsibility for the acts, omissions, conduct, representations, safety practices, suitability, or obligations of Hosts, Renters, guests, invitees, contractors, or any third party.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.2 Assumption of Risk</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  All users acknowledge and agree that they access, enter, use, and store property in a Host Space entirely at their own risk. To the extent permitted by law and subject to Section 3 (Australian Consumer Law – Consumer Guarantees), Stashly does not inspect, verify, warrant, or guarantee:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="text-gray-700">• The condition, legality, safety, structural integrity, or suitability of any Space</li>
                  <li className="text-gray-700">• The safety or condition of any Host premises or surrounding area</li>
                  <li className="text-gray-700">• The nature, storage suitability, or value of any Stored Items</li>
                  <li className="text-gray-700">• Compliance of any Space with zoning, building, health, safety, or regulatory requirements</li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.3 No Liability for Property Loss or Damage</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To the fullest extent permitted by law, and subject to Section 3, Stashly is not liable for any loss, damage, deterioration, theft, unauthorised access, contamination, destruction, or alteration involving:
                </p>
                <ul className="space-y-2 ml-4 mb-3">
                  <li className="text-gray-700">• Host property</li>
                  <li className="text-gray-700">• Renter property or Stored Items</li>
                  <li className="text-gray-700">• Third-party property</li>
                  <li className="text-gray-700">• Any structure, fixture, system, or item in or around a Space</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Regardless of the cause, including negligence, accident, structural failure, defects, water ingress, rot, pests, mould, fire, weather, or criminal activity.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed mb-2">
                    <strong>However, this clause does not apply to or limit Stashly's liability for:</strong>
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-gray-700">• Death or personal injury caused by Stashly's own negligence</li>
                    <li className="text-gray-700">• Fraud or wilful misconduct by Stashly</li>
                    <li className="text-gray-700">• Breach of consumer guarantees under the ACL</li>
                    <li className="text-gray-700">• Any loss caused by Stashly's gross negligence</li>
                  </ul>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.4 No Liability for Personal Injury or Death</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To the extent permitted by law and subject to Section 3, Stashly is not liable for any injury, illness, harm, or death suffered by any person arising from or connected to:
                </p>
                <ul className="space-y-2 ml-4 mb-3">
                  <li className="text-gray-700">• Accessing, entering, or exiting a Space</li>
                  <li className="text-gray-700">• Loading, unloading, moving, or storing property</li>
                  <li className="text-gray-700">• Hazards, defects, dangers, or conditions of a Space or surrounding property</li>
                  <li className="text-gray-700">• Conduct or negligence of any Host, Renter, guest, neighbour, contractor, or third party</li>
                </ul>
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    However, this clause does not apply to liability arising from Stashly's own negligence, fraud, or wilful misconduct, or from Stashly's breach of its non-excludable obligations under the Australian Consumer Law.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.5 Release of Claims</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To the extent permitted by law and subject to Section 3, Users release and discharge Stashly and its officers, directors, employees, contractors, and affiliates from any claim, demand, action, liability, cost, or expense arising out of or relating to:
                </p>
                <ul className="space-y-2 ml-4 mb-3">
                  <li className="text-gray-700">• The use of the Platform</li>
                  <li className="text-gray-700">• Any storage arrangement or Booking</li>
                  <li className="text-gray-700">• The condition, safety, legality, or suitability of a Space</li>
                  <li className="text-gray-700">• Theft, damage, loss, or deterioration of Stored Items</li>
                  <li className="text-gray-700">• Injury, illness, or death</li>
                </ul>
                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    However, this release does not extend to claims arising from Stashly's own negligence, fraud, or wilful misconduct, or from Stashly's breach of its non-excludable obligations under the Australian Consumer Law.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.6 Limitation of Stashly's Liability (Subject to ACL)</h4>
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2">(a) Non-Excludable Rights</h5>
                    <p className="text-gray-700">Nothing in these Terms excludes, restricts, or modifies any rights or remedies you may have under the Australian Consumer Law or any other law that cannot be excluded.</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2">(b) Capped Liability</h5>
                    <p className="text-gray-700 mb-3">To the extent permitted by law and subject to Section 3 (Australian Consumer Law – Consumer Guarantees):</p>
                    <ul className="space-y-2 ml-4">
                      <li className="text-gray-700">(i) Stashly is not liable for any loss of profit, loss of revenue, loss of business opportunity, or indirect or consequential loss, regardless of the cause</li>
                      <li className="text-gray-700">(ii) Where our liability is not otherwise excluded and the ACL allows our liability to be limited, our total aggregate liability to you for all claims arising out of or in connection with the Platform, Bookings, or our services in any 12-month period is limited to the greater of:
                        <ul className="ml-6 mt-2 space-y-1">
                          <li className="text-gray-700">- AUD 1,000; or</li>
                          <li className="text-gray-700">- the total Fees you paid to Stashly in that 12-month period</li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2">(c) Exception for Gross Negligence and Fraud</h5>
                    <p className="text-gray-700">This Section 12.6 does not apply to liability caused by Stashly's fraud, wilful misconduct, gross negligence, or breach of non-excludable consumer guarantees under the ACL.</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2">(d) Third-Party Verification Services</h5>
                    <p className="text-gray-700">To the extent permitted by law and subject to Section 3 (Australian Consumer Law), Stashly is not liable for delays, errors, or issues arising from third-party identity verification services, provided we have exercised reasonable care in selecting such services. See Section 19.6 for information about third-party verification providers.</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.7 Host Protection Programme Is Not Insurance</h4>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    The Stashly Host Protection Programme is a voluntary, discretionary support programme. It is not insurance and does not create legal rights, entitlements, or obligations. Stashly may approve, reduce, or deny assistance at its sole discretion. No user may claim compensation beyond what Stashly voluntarily provides.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.7A No Insurance for Stored Items</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Stashly does not provide insurance to Hosts or Renters. Hosts and Renters are solely responsible for obtaining and maintaining their own insurance to cover any loss, damage, or liability that may occur in connection with a Space, including damage to Host property, Renter property, and Stored Items.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Stashly is not obligated to provide insurance to protect Stored Items, and Stashly does not have insurance covering Stored Items.
                </p>
                <div className="bg-[#14B8A6]/10 border-l-4 border-[#14B8A6] p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    Stashly strongly recommends that Renters obtain appropriate insurance coverage for their Stored Items, which may be available through most insurers, or that Renters purchase an appropriate property protection or contents insurance plan suitable for the nature and value of the Stored Items being stored.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.7B Host Insurance Responsibilities</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Stashly does not provide insurance to Hosts for any Space, premises, structure, fixtures, fittings, or Host property.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Hosts are solely responsible for obtaining and maintaining any insurance they consider necessary or appropriate to protect their property, premises, and interests, including but not limited to building insurance, contents insurance, public liability insurance, and any insurance required by law, lease, mortgage, body corporate, or insurer.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Stashly does not insure Host property and does not provide coverage for loss, damage, deterioration, theft, or liability arising from the use of a Space by a Renter or any third party.
                </p>
                <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    Hosts acknowledge and agree that they list and make Spaces available entirely at their own risk and must not rely on the Platform or the Host Protection Programme as a substitute for proper insurance coverage.
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">12.8 User Responsibility and Risk Allocation</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Hosts and Renters acknowledge that they:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Assume all risks associated with using or providing a Space</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Must obtain their own insurance for property and liability risks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Must resolve disputes directly between themselves</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Are solely responsible for compliance with safety, legal, local government by-laws, zoning, and regulatory requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-gray-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Cannot rely on Stashly to protect them from loss, damage, theft, injury, or liability</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Host Protection Programme */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">13. STASHLY HOST PROTECTION PROGRAMME</h2>
              
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.1 Overview</h4>
                <p className="text-gray-700 leading-relaxed">
                  The Stashly Host Protection Programme is a voluntary, discretionary support initiative designed to provide goodwill assistance to eligible Hosts for certain damage to their property arising during a Booking. It is not an insurance policy and does not create any contractual, legal, or financial obligation for Stashly.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.2 No Legal Rights Created</h4>
                <p className="text-gray-700 leading-relaxed">
                  The Programme does not create any right, entitlement, guarantee, or promise of payment. Stashly may approve, reduce, or deny a request for assistance at its sole discretion. Decisions made under the Programme are final and are not subject to review or appeal.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.3 Eligibility Requirements</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To be considered for discretionary assistance under the Programme, a Host must:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">Have an active Booking through the Stashly Platform at the time of the incident</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">Report the incident to Stashly within 24 hours of becoming aware of it</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">Provide all requested documentation, including photos, repair quotes, receipts, and incident descriptions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">Cooperate fully with Stashly's assessment process</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">Not have contributed to or caused the damage through negligence, misconduct, or unsafe conditions</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.4 Exclusions</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Assistance under the Programme will not be considered for:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Wear and tear, gradual deterioration, or pre-existing damage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Structural failure, defects, water ingress, mould, pests, rot, or environmental conditions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Damage arising from unsafe or unsuitable Spaces</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Damage caused by the Host, their household, guests, or contractors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">High-value items, luxury goods, or items requiring specialised storage</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Consequential or indirect losses, including lost income or business interruption</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Claims made more than 30 days after the incident occurred</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.5 Assessment Process</h4>
                <p className="text-gray-700 leading-relaxed">
                  Stashly may request evidence, conduct inquiries, or engage third parties to assist in assessing a claim. Stashly may decline a request where information is incomplete, inconsistent, or insufficient to support the incident.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.6 Maximum Assistance Amount</h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  If Stashly elects to provide assistance, the total discretionary amount will not exceed:
                </p>
                <div className="bg-[#14B8A6]/10 rounded-xl p-6">
                  <ul className="space-y-2">
                    <li className="text-gray-700">• AUD 500 per Booking; or</li>
                    <li className="text-gray-700">• Another amount determined by Stashly at its discretion</li>
                  </ul>
                </div>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Payment, if any, will be made to the Host as a goodwill gesture only and is not an admission of liability.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.7 No Subrogation or Legal Pursuit</h4>
                <p className="text-gray-700 leading-relaxed">
                  Assistance provided under the Programme does not give Stashly any rights to pursue Renters, and Hosts remain responsible for seeking compensation directly from the Renter for damage or loss beyond the Programme cap.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.8 Programme May Be Modified or Withdrawn</h4>
                <p className="text-gray-700 leading-relaxed">
                  Stashly may amend, suspend, or terminate the Programme at any time without notice. Assistance requests will be assessed according to the Programme rules in place at the time of submission.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">13.9 No Insurance</h4>
                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    The Programme is not an insurance policy and must not be represented or relied upon as insurance. Hosts are solely responsible for obtaining their own property and liability insurance.
                  </p>
                </div>
              </div>
            </div>

            {/* Fees and Payments */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">14. FEES AND PAYMENTS</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">14.1 Fee Structure</h4>
                  <p className="text-gray-700">Stashly charges Fees for use of the Platform, including service fees, commissions, and other applicable charges. All amounts are in Australian dollars (AUD) unless stated otherwise.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">14.2 Renter Payment Obligations</h4>
                  <p className="text-gray-700">Renters agree to pay all Booking fees at the time of reservation. Payments are processed through Stashly's third-party payment provider.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">14.3 Host Fee Deductions</h4>
                  <p className="text-gray-700">Hosts authorise Stashly to deduct service fees and charges from payouts. Hosts will be notified of applicable fees before a Booking is confirmed.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">14.4 Payment Withholding</h4>
                  <p className="text-gray-700 mb-3">Stashly may withhold or delay payments where:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-gray-700">• A dispute exists between Host and Renter</li>
                    <li className="text-gray-700">• Fraud or misuse is suspected</li>
                    <li className="text-gray-700">• Legal compliance requires it</li>
                    <li className="text-gray-700">• Amounts are owed to Stashly by the Host</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">14.5 Payment Disputes</h4>
                  <p className="text-gray-700">Payment disputes must be raised within 30 days of the transaction. Stashly will investigate disputes in accordance with its payment processor's terms.</p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-600 rounded-r-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">14.6 Circumvention of the Platform and Fees</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Users must not, directly or indirectly, attempt to circumvent the Stashly Platform, Fees, or payment systems.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Without limitation, Hosts and Renters must not:
                  </p>
                  <ul className="space-y-3 mb-4">
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(a) enter into, propose, encourage, or complete a storage arrangement outside of the Platform that originates from a listing, enquiry, Booking, message, or interaction made through Stashly;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(b) exchange contact details, make arrangements, or enter into agreements for the purpose of avoiding or reducing Fees payable to Stashly;</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(c) accept or make payment outside of the Platform for any storage arrangement that was introduced, facilitated, or made possible through Stashly; or</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                      <span className="text-gray-700">(d) otherwise seek to deprive Stashly of Fees to which it would reasonably be entitled.</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Any storage arrangement entered into in breach of this clause is a material breach of these Terms. Without limiting any other rights, Stashly may permanently suspend or terminate the Account of any Host or Renter who breaches Section 14.6, without notice, and may refuse future access to the Platform.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Where Stashly reasonably determines that a User has circumvented the Platform in breach of Section 14.6, Stashly may recover any unpaid Fees that would have been payable had the transaction occurred through the Platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Cancellations and Refunds */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">15. CANCELLATIONS AND REFUNDS</h2>
              
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">15.1 Cancellation Rules</h4>
                <p className="text-gray-700 leading-relaxed">
                  Hosts and Renters must comply with the cancellation rules displayed at the time of Booking, except where the Australian Consumer Law or other mandatory laws give the Renter additional rights (for example, where there is a major failure in the provision of services or breach of consumer guarantees).
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">15.2 Statutory Refund Rights</h4>
                <p className="text-gray-700 leading-relaxed">
                  Stashly will provide refunds or other remedies where required under the Australian Consumer Law or any other Applicable Law and may provide refunds in other circumstances in accordance with its policies.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">15.3 Discretionary Refund Denial</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Subject to clause 15.2, Stashly may decline a refund where:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">(a) the Renter has already had the benefit of the Space in line with the Booking and accessed it without complaint;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">(b) the Host has reasonably relied on the Booking and incurred loss or cost;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">(c) misuse, dishonesty, or breach of these Terms is suspected;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-600 text-xl mt-1"></i>
                    <span className="text-gray-700">(d) the refund request is made more than 30 days after the Booking commenced, unless Stashly is otherwise required by law to accept it.</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">15.4 Major Failure in Services</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If a Renter is entitled to a remedy for a major failure in services under the Australian Consumer Law (including failure to deliver substantially what was promised in the Listing), Stashly will, at its discretion:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">(a) allow cancellation of the Booking and issue a full refund;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">(b) arrange an alternative Space at no additional cost;</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-gray-700">(c) provide such other remedy as is appropriate in the circumstances.</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">15.5 How to Request a Refund</h4>
                <p className="text-gray-700 leading-relaxed">
                  Requests for refunds must be made in writing to support@stashly.com.au and must include details of the alleged failure or breach. Stashly will respond within 10 Business Days.
                </p>
              </div>
            </div>

            {/* Disputes Between Users */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">16. DISPUTES BETWEEN USERS</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">16.1 Direct Resolution</h4>
                  <p className="text-gray-700">Hosts and Renters are responsible for resolving disputes directly between themselves. We encourage communication and good faith negotiation.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">16.2 Stashly's Role</h4>
                  <p className="text-gray-700">Stashly may, but is not obliged to, assist in facilitating communication between Users or providing information relevant to a dispute.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">16.3 No Obligation to Mediate</h4>
                  <p className="text-gray-700">Stashly is not responsible for the outcome of any dispute and has no obligation to mediate or resolve disagreements between Users, except where required by law or these Terms.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">16.4 Legal Resolution</h4>
                  <p className="text-gray-700">Either party may seek resolution through mediation, arbitration, or court proceedings in accordance with Section 21 (Governing Law and Jurisdiction) below.</p>
                </div>
              </div>
            </div>

            {/* Suspension and Termination */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">17. SUSPENSION AND TERMINATION</h2>
              
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">17.1 Grounds for Suspension or Termination</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Stashly may suspend or terminate a User's Account where:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-orange-600 text-xl mt-1"></i>
                    <span className="text-gray-700">These Terms are breached</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-orange-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Unsafe, unlawful, or dishonest conduct occurs</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-orange-600 text-xl mt-1"></i>
                    <span className="text-gray-700">False or misleading information is provided</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-orange-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Payment issues persist after notice</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-orange-600 text-xl mt-1"></i>
                    <span className="text-gray-700">The User is deemed unsuitable to use the Platform</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-orange-600 text-xl mt-1"></i>
                    <span className="text-gray-700">Required by law</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">17.2 Notice of Termination</h4>
                <p className="text-gray-700 leading-relaxed">
                  Where practicable, Stashly will provide written notice of the reason for suspension or termination. In cases involving safety risk or illegal activity, suspension may be immediate.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">17.3 User-Initiated Termination</h4>
                <p className="text-gray-700 leading-relaxed">
                  Users may terminate their Account at any time by contacting Stashly in writing. Termination does not affect any existing obligations, including Fees owed or liability for prior conduct.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">17.4 Effect of Termination</h4>
                <p className="text-gray-700 leading-relaxed">
                  Upon termination, all rights granted under these Terms cease, but Sections relating to liability, disclaimers, indemnities, intellectual property, and payment obligations survive.
                </p>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">18. INTELLECTUAL PROPERTY</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">18.1</h4>
                  <p className="text-gray-700">All Platform content, branding, logos, text, images, software, and materials are owned by or licensed to Stashly.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">18.2</h4>
                  <p className="text-gray-700">Users may not copy, reproduce, distribute, or create derivative works without written consent from Stashly.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">18.3</h4>
                  <p className="text-gray-700">Users grant Stashly a non-exclusive, worldwide licence to use content they upload (such as photos of Spaces or reviews) for Platform operation, marketing, and quality assurance purposes.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">18.4</h4>
                  <p className="text-gray-700">Stashly will not share personal information with third parties except as required by law, for payment processing, identity verification, or to prevent fraud.</p>
                </div>
              </div>
            </div>

            {/* Privacy and Data */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">19. PRIVACY AND DATA</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">19.1</h4>
                  <p className="text-gray-700">Stashly collects and handles personal information in accordance with its Privacy Policy and the Privacy Act 1988 (Cth), including the Australian Privacy Principles (APPs).</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">19.2</h4>
                  <p className="text-gray-700">By using the Platform, Users consent to the collection, storage, and use of their personal information as outlined in the Privacy Policy, which is available on the Platform.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">19.3</h4>
                  <p className="text-gray-700">Users must comply with privacy and data protection laws when handling information obtained through the Platform (including Renter contact details obtained by Hosts).</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">19.4</h4>
                  <p className="text-gray-700">Stashly will not share personal information with third parties except as required by law, for payment processing, identity verification, or to prevent fraud.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">19.5</h4>
                  <p className="text-gray-700">Users have the right to access and request correction of their personal information under the Australian Privacy Principles. Contact support@stashly.com.au to exercise these rights.</p>
                </div>

                {/* NEW Section 19.6 - Identity Verification */}
                <div className="mt-8 pt-8 border-t-2 border-gray-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">19.6 IDENTITY VERIFICATION AND DOCUMENT HANDLING</h3>
                  
                  <div className="space-y-6">
                    {/* 19.6.1 */}
                    <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.1 Verification Requirement</h4>
                      <p className="text-gray-700 leading-relaxed">
                        We may require identity verification to access certain platform features, including listing spaces, making bookings, or processing payments. When verification is requested, it is mandatory and is a condition of platform use. This requirement applies to both Hosts and Renters as appropriate to their platform activities.
                      </p>
                    </div>

                    {/* 19.6.2 */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.2 Required Documents</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">For verification, we may request:</p>
                      <ul className="space-y-2 ml-4">
                        <li className="text-gray-700">• Government-issued photo ID (driver's license, passport, or national ID)</li>
                        <li className="text-gray-700">• Proof of address dated within 90 days (utility bill, bank statement, or government correspondence)</li>
                        <li className="text-gray-700">• Selfie photograph for identity matching</li>
                        <li className="text-gray-700">• Additional documents if required for compliance or fraud prevention</li>
                      </ul>
                    </div>

                    {/* 19.6.3 */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.3 Document Submission Requirements</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">By submitting verification documents, you:</p>
                      <ul className="space-y-2 ml-4">
                        <li className="text-gray-700">• Confirm all documents are authentic, unaltered, and belong to you</li>
                        <li className="text-gray-700">• Grant permission to process, store, and review documents for verification purposes</li>
                        <li className="text-gray-700">• Authorize use of third-party verification services (e.g., Stripe Identity)</li>
                        <li className="text-gray-700">• Acknowledge that false or fraudulent documents will result in immediate account termination and potential legal action</li>
                        <li className="text-gray-700">• Understand submission does not guarantee approval</li>
                      </ul>
                    </div>

                    {/* 19.6.4 */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.4 Security and Processing</h4>
                      <p className="text-gray-700 font-semibold mb-2">Security Measures:</p>
                      <ul className="space-y-2 ml-4 mb-4">
                        <li className="text-gray-700">• AES-256 encryption for all document transmission and storage</li>
                        <li className="text-gray-700">• Access-controlled secure storage with comprehensive audit logging</li>
                        <li className="text-gray-700">• Restricted access limited to authorized personnel only</li>
                        <li className="text-gray-700">• Multi-factor authentication and role-based access controls</li>
                      </ul>
                      <p className="text-gray-700 font-semibold mb-2">Review Process:</p>
                      <ul className="space-y-2 ml-4">
                        <li className="text-gray-700">• Documents reviewed within 24-48 hours</li>
                        <li className="text-gray-700">• Email notification upon completion (approved or rejected)</li>
                        <li className="text-gray-700">• Opportunity to resubmit if rejected with reason provided</li>
                      </ul>
                    </div>

                    {/* 19.6.5 */}
                    <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.5 Automatic 30-Day Deletion Policy</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">Upon successful verification:</p>
                      <ul className="space-y-2 ml-4 mb-4">
                        <li className="text-gray-700">• Documents retained for <strong>30 calendar days</strong> from approval date</li>
                        <li className="text-gray-700">• <strong>Automatic permanent deletion</strong> after 30 days from all systems, backups, and storage</li>
                        <li className="text-gray-700">• Email confirmation sent upon deletion</li>
                        <li className="text-gray-700">• Verification audit log retained (containing no personal identifying information from documents)</li>
                      </ul>
                      <p className="text-gray-700 font-semibold mb-2">Immediate Deletion:</p>
                      <ul className="space-y-2 ml-4">
                        <li className="text-gray-700">• Request immediate deletion anytime via account settings</li>
                        <li className="text-gray-700">• Processed within 24 hours</li>
                        <li className="text-gray-700">• May affect verified status and platform access</li>
                      </ul>
                    </div>

                    {/* 19.6.6 */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.6 Third-Party Verification Services</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">We may use third-party identity verification providers who:</p>
                      <ul className="space-y-2 ml-4 mb-3">
                        <li className="text-gray-700">• Are bound by strict data protection agreements</li>
                        <li className="text-gray-700">• Process documents solely for verification purposes</li>
                        <li className="text-gray-700">• Do not retain documents beyond verification</li>
                        <li className="text-gray-700">• Comply with GDPR, CCPA, and applicable data protection laws</li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed">
                        We never sell, rent, or share verification documents for marketing purposes.
                      </p>
                    </div>

                    {/* 19.6.7 */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.7 User Rights Regarding Verification Documents</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">You have the right to:</p>
                      <ul className="space-y-2 ml-4 mb-3">
                        <li className="text-gray-700">• Access copies of submitted documents</li>
                        <li className="text-gray-700">• Request correction of inaccurate information</li>
                        <li className="text-gray-700">• Request immediate deletion at any time</li>
                        <li className="text-gray-700">• Object to processing (may restrict platform features)</li>
                        <li className="text-gray-700">• Request data in machine-readable format</li>
                        <li className="text-gray-700">• Lodge complaints with data protection authorities</li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed">
                        Contact privacy@stashly.com.au or use account settings to exercise these rights.
                      </p>
                    </div>

                    {/* 19.6.8 */}
                    <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.8 Legal Compliance and Disclosure</h4>
                      <p className="text-gray-700 leading-relaxed mb-3">We may retain or disclose documents beyond 30 days only if:</p>
                      <ul className="space-y-2 ml-4 mb-3">
                        <li className="text-gray-700">• Required by law, court order, or government regulation</li>
                        <li className="text-gray-700">• Necessary for AML/KYC compliance</li>
                        <li className="text-gray-700">• Required to investigate fraud, security incidents, or Terms violations</li>
                        <li className="text-gray-700">• Needed to protect rights, property, or safety of users or the public</li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed">
                        Retention limited to minimum necessary information for minimum required time.
                      </p>
                    </div>

                    {/* 19.6.9 */}
                    <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.9 Consequences of Non-Compliance</h4>
                      <p className="text-gray-700 font-semibold mb-2">Failure to verify when required:</p>
                      <ul className="space-y-2 ml-4 mb-4">
                        <li className="text-gray-700">• Restricted platform access</li>
                        <li className="text-gray-700">• Inability to list spaces or make bookings</li>
                        <li className="text-gray-700">• Suspended earnings withdrawals</li>
                        <li className="text-gray-700">• Temporary or permanent account suspension</li>
                      </ul>
                      <p className="text-gray-700 font-semibold mb-2">Providing false/fraudulent documents:</p>
                      <ul className="space-y-2 ml-4">
                        <li className="text-gray-700">• Immediate account termination</li>
                        <li className="text-gray-700">• Forfeiture of pending earnings or deposits</li>
                        <li className="text-gray-700">• Reporting to authorities</li>
                        <li className="text-gray-700">• Potential legal action</li>
                      </ul>
                    </div>

                    {/* 19.6.10 */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h4 className="font-bold text-gray-900 mb-3">19.6.10 Policy Changes</h4>
                      <p className="text-gray-700 leading-relaxed">
                        We may modify verification requirements at any time. Material changes communicated via email and platform notice. Continued use after changes constitutes acceptance.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">20. CHANGES TO TERMS</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">20.1 Amendment Rights</h4>
                  <p className="text-gray-700">Stashly may amend these Terms at any time by posting the revised version on the Platform and updating the "Last updated" date.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">20.2 Acceptance of Changes</h4>
                  <p className="text-gray-700">Continued use of the Platform after changes take effect constitutes acceptance of the updated Terms, to the extent permitted by law.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">20.3 Notification of Changes</h4>
                  <p className="text-gray-700">Stashly will make reasonable efforts to notify Users of material changes via email or in-Platform notification.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">20.4 Material Changes</h4>
                  <p className="text-gray-700">Where changes materially prejudice Users' rights, Stashly will provide at least 30 days' notice before the changes take effect.</p>
                </div>
              </div>
            </div>

            {/* Non-Payment and Abandoned Property */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">20A. NON-PAYMENT, DEFAULT AND ABANDONED PROPERTY</h2>
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.1 Payment Obligations</h4>
                  <p className="text-gray-700 leading-relaxed">A Renter is in default if any payment is not received within 7 days of its due date, or if a Renter&apos;s payment method fails, is cancelled, charged back, or reversed for any reason.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.2 Notice of Default</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Upon default, Stashly will issue written notice to the Renter specifying: (a) the amount outstanding; (b) a cure period of no less than 14 days to remedy the default; (c) the consequences of failing to remedy the default, including termination of the Booking and restrictions on access to the Space.</p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-3">20A.3 Consequences of Default</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">If a Renter fails to remedy a default within the cure period, Stashly and/or the Host may: (a) immediately terminate the Booking; (b) restrict or deny the Renter&apos;s access to the Space until all outstanding amounts are paid; (c) suspend or permanently terminate the Renter&apos;s Account; (d) pursue recovery of all outstanding amounts through any available legal means.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.4 Renter&apos;s Obligation to Remove Stored Items</h4>
                  <p className="text-gray-700 leading-relaxed">Upon termination of a Booking for any reason, the Renter must remove all Stored Items from the Space within 14 days (the Removal Period). The Renter remains liable for all costs that accrue during the Removal Period.</p>
                </div>
                <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-3">20A.5 Abandoned Property</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Stored Items will be deemed abandoned if the Renter: (a) fails to remove Stored Items within the Removal Period; (b) cannot be contacted after reasonable attempts for 30 days following termination; or (c) expressly communicates an intention not to retrieve the Stored Items. Before treating items as abandoned, Stashly will make reasonable attempts to contact the Renter via all contact details on the Platform.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.6 Dealing with Abandoned Property</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">Where Stored Items are deemed abandoned, the Host and/or Stashly may, subject to applicable state or territory legislation: (a) issue a final written notice giving 14 days to collect items before disposal or sale; (b) arrange removal or disposal at the Renter&apos;s cost; (c) sell Stored Items and apply proceeds to outstanding amounts; (d) donate or destroy items of negligible value. Any surplus sale proceeds will be held for the Renter for 90 days.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.7 Vehicles and High-Value Items</h4>
                  <p className="text-gray-700 leading-relaxed mb-3">Where Stored Items include a registered motor vehicle, boat, trailer, or caravan, additional legal obligations may apply. The Host must not tow, move, sell, or deal with a Renter&apos;s registered vehicle without first: (a) issuing written notice to the Renter; (b) allowing the full Removal Period to expire; (c) notifying Stashly and obtaining guidance on applicable state laws. Stashly strongly recommends Hosts seek independent legal advice before taking any action regarding abandoned vehicles, as the legal process varies by state and territory in Australia.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.8 Host&apos;s Right to Recover Costs</h4>
                  <p className="text-gray-700 leading-relaxed">The Renter is liable to the Host and/or Stashly for all reasonable costs incurred in connection with a default or abandonment, including unpaid Booking fees, ongoing storage charges during the Removal Period, costs of removal, towing, storage, disposal or sale of Stored Items, and legal costs incurred in recovering outstanding amounts.</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-bold text-gray-900 mb-3">20A.9 Stashly&apos;s Role in Default Situations</h4>
                  <p className="text-gray-700 leading-relaxed mb-4">While Stashly is not a party to the storage agreement, Stashly may assist by: (a) sending notices to the Renter on behalf of the Host via the Platform; (b) withholding access to the Renter&apos;s Account until outstanding amounts are resolved; (c) providing the Host with the Renter&apos;s contact details for recovery purposes, to the extent permitted by the Privacy Act 1988 (Cth); (d) referring the matter to a debt recovery service where appropriate.</p>
                </div>
                <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-3">20A.10 State and Territory Law</h4>
                  <p className="text-gray-700 leading-relaxed">The rights in this Section 20A are subject to mandatory requirements under state or territory legislation governing storage liens, abandoned goods, unclaimed property, and disposal of motor vehicles. Where state or territory law imposes additional requirements, the Host must comply with those requirements. Users are encouraged to obtain independent legal advice regarding their rights under applicable state or territory law.</p>
                </div>
              </div>
            </div>

            {/* Governing Law and Jurisdiction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">21. GOVERNING LAW AND JURISDICTION</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">21.1 Governing Law</h4>
                  <p className="text-gray-700 mb-3">These Terms are governed by the laws of Australia and, to the extent relevant, the laws of the state or territory in which the Space is located, including the Australian Consumer Law in Schedule 2 of the Competition and Consumer Act 2010 (Cth).</p>
                  <p className="text-gray-700">Our document handling and data protection practices comply with applicable data protection laws, including the Privacy Act 1988 (Cth), Australian Privacy Principles, GDPR (for EU users), and CCPA (for California residents). Identity verification procedures are detailed in Section 19.6.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">21.2 Jurisdiction</h4>
                  <p className="text-gray-700 mb-3">Users submit to the non-exclusive jurisdiction of:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-gray-700">• The courts of the state or territory in which the Space is located</li>
                    <li className="text-gray-700">• The Federal Court of Australia</li>
                    <li className="text-gray-700">• Their appellate courts</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">21.3 No Mandatory Arbitration</h4>
                  <p className="text-gray-700">Nothing in these Terms requires users to submit disputes to arbitration. Users may pursue disputes through mediation, Australian courts, or other dispute resolution mechanisms agreed in writing.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">21.4 Severability</h4>
                  <p className="text-gray-700">If any provision of these Terms is found invalid or unenforceable by a court, the remaining provisions continue in full force. If a provision is found to be unenforceable with respect to consumers, Stashly will modify it to the extent necessary to make it enforceable while preserving its intent.</p>
                </div>
              </div>
            </div>

            {/* General Provisions */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">22. GENERAL PROVISIONS</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.1 Entire Agreement</h4>
                  <p className="text-gray-700">These Terms constitute the entire agreement between Users and Stashly regarding use of the Platform and supersede any prior understandings or agreements, except where a written agreement has been signed by both parties for a specific Booking or dispute.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.2 Assignment</h4>
                  <p className="text-gray-700">Users may not assign or transfer their rights or obligations under these Terms without Stashly's written consent. Stashly may assign its rights or obligations at any time without notice, provided it does so in a manner consistent with Users' privacy and security.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.3 Waiver</h4>
                  <p className="text-gray-700">A failure or delay by Stashly to enforce any provision of these Terms does not operate as a waiver of that provision or any other provision.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.4 Force Majeure</h4>
                  <p className="text-gray-700">Stashly is not liable for any failure or delay caused by events beyond its reasonable control, including natural disasters, war, labour disputes, power outages, telecommunications failures, or pandemics.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.5 Relationship of Parties</h4>
                  <p className="text-gray-700">Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship between Stashly and any User. Hosts and Renters are independent parties responsible for their own conduct.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.6 No Professional Advice</h4>
                  <p className="text-gray-700">Stashly does not provide legal, financial, insurance, or tax advice. Users should obtain their own professional advice where needed, particularly regarding insurance, tax obligations, and compliance with local laws.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.7 Survival</h4>
                  <p className="text-gray-700">The following provisions survive termination of these Terms: Sections relating to limitation of liability, disclaimers, indemnities, intellectual property, payment obligations, dispute resolution, governing law, and any other provision that by its nature is intended to survive.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.8 Interpretation</h4>
                  <ul className="space-y-2">
                    <li className="text-gray-700">• Headings are for convenience only and do not affect interpretation</li>
                    <li className="text-gray-700">• References to "including" mean "including without limitation"</li>
                    <li className="text-gray-700">• A reference to a section is a reference to a section of these Terms</li>
                    <li className="text-gray-700">• Words in the singular include the plural and vice versa</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.9 Notices</h4>
                  <ul className="space-y-2 mb-3">
                    <li className="text-gray-700">• Stashly may provide notices electronically via the Platform, email, or SMS to the contact details you provide</li>
                    <li className="text-gray-700">• Users are responsible for keeping their contact details current on the Platform</li>
                    <li className="text-gray-700">• Notice is deemed given when sent, or if sent electronically, when it is displayed on the Platform or in the email/SMS inbox</li>
                    <li className="text-gray-700">• Users may provide notice to Stashly by email to support@stashly.com.au or by post to the address displayed on the Platform</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.10 Electronic Communications</h4>
                  <p className="text-gray-700">Users consent to receiving communications electronically and agree that electronic notices (including email and in-Platform notifications) satisfy any legal requirement for written notice.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-2">22.11 Compliance with Law</h4>
                  <p className="text-gray-700">Users must comply with all Applicable Laws in their use of the Platform, including consumer protection laws, anti-discrimination laws, privacy laws, and zoning or planning laws.</p>
                </div>
              </div>
            </div>

            {/* Appendix A */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">APPENDIX A – DEFINITIONS RECAP TABLE</h2>
              <div className="bg-gray-50 rounded-xl p-6 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="py-3 pr-4 text-left font-bold text-gray-900">Term</th>
                      <th className="py-3 pr-4 text-left font-bold text-gray-900">Defined In</th>
                      <th className="py-3 text-left font-bold text-gray-900">Plain-Language Summary</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Account</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">Your registered profile used to access Stashly.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Booking</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">A confirmed reservation of a Space for storage.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Host</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">A user offering space for storage.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Renter</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">A user storing items in a Host's space.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Space</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">Any area listed by a Host for storage.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Stored Items</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">The goods a Renter places in a Space.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Platform</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">The Stashly website and mobile app.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Host Protection Programme</td>
                      <td className="py-3 pr-4 text-gray-700">Section 14</td>
                      <td className="py-3 text-gray-700">A discretionary support programme for Hosts; not insurance.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Fees</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">Charges payable for using Stashly's services.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Applicable Law</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">The laws that apply to the Platform and Users.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Consumer</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">A person acquiring services for personal/household use.</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-900">Australian Consumer Law</td>
                      <td className="py-3 pr-4 text-gray-700">Section 1</td>
                      <td className="py-3 text-gray-700">The ACL in Schedule 2 of the Competition and Consumer Act 2010 (Cth).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">CONTACT INFORMATION</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about these Terms or your use of the Stashly Platform, please contact us:
              </p>
              <div className="bg-gradient-to-br from-[#14B8A6] to-[#0F9B8E] text-white rounded-xl p-8">
                <p className="text-xl font-bold mb-4">Stashly Technologies Pty Ltd</p>
                <p className="mb-2"><strong>ABN:</strong> 34 696 643 713</p>
                <p className="mb-2"><strong>Email:</strong> support@stashly.com.au</p>
                <p className="mb-2"><strong>Phone:</strong> +61 408 199 679</p>
                <p><strong>Address:</strong> 12 Charlwood Close, Lynbrook VIC 3975, Australia</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Have Questions About Our Terms?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our support team is here to help clarify any terms or policies
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9B8E] transition-colors whitespace-nowrap cursor-pointer"
          >
            Contact Support
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
